import { createApp, defineComponent, h, reactive, type App, type Component } from 'vue'
import { createPinia, type Pinia } from 'pinia'

import { installUserSettings } from '../../userSettings/installUserSettings'

declare global {
  interface Window {
    __perlingoDeckPlayerPinia__?: Pinia
  }
}

type TargetInput = string | Element

interface MountLifecycleContext {
  target: Element
  app: App
  pinia: Pinia
}

export interface MountDeckPlayerWidgetOptions {
  /** Optional global Pinia instance to reuse across widgets */
  pinia?: Pinia
  /** Props forwarded to the rendered widget component */
  props?: Record<string, unknown>
  /** Skip duplicate mount safeguard and allow remounting on the same target */
  allowRemount?: boolean
  /** Human readable name used in logs and target markers */
  displayName?: string
  beforeMount?: (context: MountLifecycleContext) => void | Promise<void>
  afterMount?: (context: MountLifecycleContext) => void | Promise<void>
  onError?: (error: unknown) => void
}

export interface MountDeckPlayerWidgetHandle extends MountLifecycleContext {
  unmount: () => void
  updateProps: (updates: Record<string, unknown>) => void
}

const DATA_MARKER = 'data-deckplayer-widget-mounted'
const GLOBAL_PINIA_KEY = '__perlingoDeckPlayerPinia__'

const resolveSharedPinia = (provided?: Pinia): Pinia => {
  if (provided) return provided
  if (typeof window !== 'undefined') {
    if (!window[GLOBAL_PINIA_KEY]) {
      window[GLOBAL_PINIA_KEY] = createPinia()
    }
    return window[GLOBAL_PINIA_KEY]!
  }
  return createPinia()
}

const resolveTarget = (target: TargetInput): Element | null => {
  if (typeof target === 'string') return document.querySelector(target)
  return target
}

export async function mountDeckPlayerWidget(
  targetInput: TargetInput,
  component: Component,
  options: MountDeckPlayerWidgetOptions = {},
): Promise<MountDeckPlayerWidgetHandle> {
  const target = resolveTarget(targetInput)
  const label = options.displayName ?? 'deckplayer-widget'

  if (!target) {
    const error = new Error(`mountDeckPlayerWidget: target not found for "${label}"`)
    options.onError?.(error)
    throw error
  }

  const existingMarker = target.getAttribute(DATA_MARKER)
  if (existingMarker && !options.allowRemount) {
    const error = new Error(`mountDeckPlayerWidget: target already used by ${existingMarker}`)
    options.onError?.(error)
    throw error
  }

  const pinia = resolveSharedPinia(options.pinia)

  const propsState = reactive(options.props ?? {})

  const Wrapped = defineComponent({
    name: `DeckPlayerWidgetWrapper-${label}`,
    setup() {
      return () => h(component, { ...propsState })
    },
    errorCaptured(err) {
      options.onError?.(err)
      return false
    },
  })

  const app = createApp(Wrapped)
  app.use(pinia)
  installUserSettings(app, pinia)

  const context: MountLifecycleContext = { target, app, pinia }

  if (options.beforeMount) await options.beforeMount(context)

  app.mount(target)
  target.setAttribute(DATA_MARKER, label)

  if (options.afterMount) await options.afterMount(context)

  return {
    ...context,
    updateProps: (updates: Record<string, unknown>) => Object.assign(propsState, updates),
    unmount: () => {
      app.unmount()
      target.removeAttribute(DATA_MARKER)
      if (!options.allowRemount) {
        target.innerHTML = ''
      }
    },
  }
}
